import { useEffect, useState, type FormEvent } from "react";
import type { AppState, CleaningTask, Member, ScheduleType, TaskInput } from "../types";
import { downloadExport } from "../lib/api";
import { defaultTaskInput, inputFromTask, normalizeScheduleValue, scheduleLabel, WEEKDAYS } from "../lib/schedule";
import { ChevronIcon, LinkIcon, UserIcon } from "./Icons";

interface SettingsViewProps {
  state: AppState;
  token: string;
  selectedMemberId: string;
  busy: boolean;
  onSelectMember: (memberId: string) => void;
  onUpdateMembers: (members: Pick<Member, "id" | "name">[]) => Promise<void>;
  onCreateTask: (input: TaskInput) => Promise<void>;
  onUpdateTask: (taskId: string, input: TaskInput) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  onReorderTasks: (taskIds: string[]) => Promise<void>;
  onCopyShareUrl: () => Promise<void>;
  onRotateToken: () => Promise<void>;
  onMessage: (message: string, type?: "success" | "error") => void;
}

export function SettingsView({
  state,
  token,
  selectedMemberId,
  busy,
  onSelectMember,
  onUpdateMembers,
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
  onReorderTasks,
  onCopyShareUrl,
  onRotateToken,
  onMessage,
}: SettingsViewProps) {
  const [memberNames, setMemberNames] = useState(state.members.map((member) => member.name));
  const activeTasks = state.tasks.filter((task) => task.is_active);

  useEffect(() => setMemberNames(state.members.map((member) => member.name)), [state.members]);

  const saveMembers = async (event: FormEvent) => {
    event.preventDefault();
    await onUpdateMembers(state.members.map((member, index) => ({ id: member.id, name: memberNames[index] })));
  };

  const moveTask = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= activeTasks.length) return;
    const ids = activeTasks.map((task) => task.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    await onReorderTasks(ids);
  };

  const exportBackup = async () => {
    try {
      await downloadExport(token);
      onMessage("バックアップをダウンロードしました。", "success");
    } catch (error) {
      onMessage(error instanceof Error ? error.message : "バックアップに失敗しました。", "error");
    }
  };

  return (
    <div className="settings-stack">
      <section className="settings-card">
        <div className="section-heading">
          <span className="section-icon"><UserIcon /></span>
          <div><p className="eyebrow">MEMBERS</p><h2>ふたりの設定</h2></div>
        </div>
        <form onSubmit={saveMembers} className="settings-form">
          {state.members.map((member, index) => (
            <label key={member.id}>
              <span>{index + 1}人目の名前</span>
              <input
                value={memberNames[index] ?? ""}
                maxLength={30}
                onChange={(event) => {
                  const next = [...memberNames];
                  next[index] = event.target.value;
                  setMemberNames(next);
                }}
              />
            </label>
          ))}
          <button className="secondary-button" disabled={busy || memberNames.some((name) => !name.trim())}>名前を保存</button>
        </form>
        <div className="device-user-setting">
          <span>このスマートフォンを使う人</span>
          <div className="segmented-control">
            {state.members.map((member) => (
              <button
                key={member.id}
                className={selectedMemberId === member.id ? "is-active" : ""}
                onClick={() => onSelectMember(member.id)}
              >{member.name}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="settings-card">
        <div className="section-heading compact-heading">
          <div><p className="eyebrow">CLEANING ITEMS</p><h2>掃除項目</h2></div>
          <span className="item-count">{activeTasks.length}項目</span>
        </div>
        <div className="task-settings-list">
          {activeTasks.map((task, index) => (
            <TaskEditor
              key={task.id}
              task={task}
              busy={busy}
              canMoveUp={index > 0}
              canMoveDown={index < activeTasks.length - 1}
              onMove={(direction) => moveTask(index, direction)}
              onSave={(input) => onUpdateTask(task.id, input)}
              onDelete={() => onDeleteTask(task.id)}
            />
          ))}
        </div>
        <NewTaskForm busy={busy} onCreate={onCreateTask} />
      </section>

      <section className="settings-card">
        <div className="section-heading">
          <span className="section-icon"><LinkIcon /></span>
          <div><p className="eyebrow">SHARING & DATA</p><h2>共有とデータ</h2></div>
        </div>
        <p className="settings-description">共有URLを知っている人は、すべての記録を閲覧・編集できます。</p>
        <div className="settings-actions">
          <button className="secondary-button" onClick={() => void onCopyShareUrl()}>共有URLをコピー</button>
          <button className="secondary-button" onClick={() => void exportBackup()}>JSONバックアップ</button>
          <button
            className="danger-text-button"
            onClick={() => {
              if (window.confirm("現在の共有URLは使えなくなります。新しく発行しますか？")) void onRotateToken();
            }}
          >共有URLを再発行</button>
        </div>
      </section>
    </div>
  );
}

function TaskEditor({
  task,
  busy,
  canMoveUp,
  canMoveDown,
  onMove,
  onSave,
  onDelete,
}: {
  task: CleaningTask;
  busy: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (direction: -1 | 1) => Promise<void>;
  onSave: (input: TaskInput) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState<TaskInput>(() => inputFromTask(task));
  useEffect(() => setInput(inputFromTask(task)), [task]);

  if (!editing) {
    return (
      <div className="task-setting-row">
        <div className="order-buttons">
          <button aria-label={`${task.name}を上へ`} disabled={!canMoveUp || busy} onClick={() => void onMove(-1)}><ChevronIcon direction="up" /></button>
          <button aria-label={`${task.name}を下へ`} disabled={!canMoveDown || busy} onClick={() => void onMove(1)}><ChevronIcon direction="down" /></button>
        </div>
        <button className="task-setting-summary" onClick={() => setEditing(true)}>
          <strong>{task.name}</strong><small>{scheduleLabel(task)}</small>
        </button>
        <button className="text-button" onClick={() => setEditing(true)}>編集</button>
      </div>
    );
  }

  return (
    <div className="task-editor-panel">
      <TaskFields value={input} onChange={setInput} />
      <div className="editor-actions">
        <button
          className="danger-text-button"
          disabled={busy}
          onClick={() => {
            if (window.confirm(`「${task.name}」を削除しますか？過去の記録は残ります。`)) void onDelete();
          }}
        >削除</button>
        <span className="spacer" />
        <button className="text-button" onClick={() => { setInput(inputFromTask(task)); setEditing(false); }}>キャンセル</button>
        <button
          className="secondary-button small-button"
          disabled={busy || !input.name.trim()}
          onClick={async () => { await onSave(input); setEditing(false); }}
        >保存</button>
      </div>
    </div>
  );
}

function NewTaskForm({ busy, onCreate }: { busy: boolean; onCreate: (input: TaskInput) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState<TaskInput>(defaultTaskInput());

  if (!open) return <button className="add-task-button" onClick={() => setOpen(true)}>＋ 掃除項目を追加</button>;

  return (
    <div className="task-editor-panel new-task-panel">
      <h3>新しい掃除項目</h3>
      <TaskFields value={input} onChange={setInput} />
      <div className="editor-actions">
        <span className="spacer" />
        <button className="text-button" onClick={() => { setOpen(false); setInput(defaultTaskInput()); }}>キャンセル</button>
        <button
          className="secondary-button small-button"
          disabled={busy || !input.name.trim()}
          onClick={async () => {
            await onCreate(input);
            setInput(defaultTaskInput());
            setOpen(false);
          }}
        >追加</button>
      </div>
    </div>
  );
}

function TaskFields({ value, onChange }: { value: TaskInput; onChange: (value: TaskInput) => void }) {
  const changeType = (scheduleType: ScheduleType) => {
    onChange({ ...value, scheduleType, scheduleValue: normalizeScheduleValue(scheduleType, value.scheduleValue) });
  };

  return (
    <div className="task-fields">
      <label>
        <span>項目名</span>
        <input value={value.name} maxLength={50} onChange={(event) => onChange({ ...value, name: event.target.value })} />
      </label>
      <label>
        <span>掃除の目安</span>
        <select value={value.scheduleType} onChange={(event) => changeType(event.target.value as ScheduleType)}>
          <option value="none">周期なし</option>
          <option value="daily">毎日</option>
          <option value="weekly">毎週</option>
          <option value="weekdays">曜日指定</option>
          <option value="interval">一定日数ごと</option>
        </select>
      </label>
      {value.scheduleType === "weekly" && (
        <label>
          <span>曜日</span>
          <select
            value={value.scheduleValue.weekday ?? 1}
            onChange={(event) => onChange({ ...value, scheduleValue: { weekday: Number(event.target.value) } })}
          >
            {WEEKDAYS.map((label, day) => <option key={label} value={day}>{label}曜日</option>)}
          </select>
        </label>
      )}
      {value.scheduleType === "weekdays" && (
        <fieldset className="weekday-selector">
          <legend>曜日</legend>
          {WEEKDAYS.map((label, day) => {
            const selected = value.scheduleValue.weekdays?.includes(day) ?? false;
            return (
              <label key={label} className={selected ? "is-selected" : ""}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => {
                    const days = new Set(value.scheduleValue.weekdays ?? []);
                    selected ? days.delete(day) : days.add(day);
                    if (!days.size) days.add(day);
                    onChange({ ...value, scheduleValue: { weekdays: [...days].sort() } });
                  }}
                />
                {label}
              </label>
            );
          })}
        </fieldset>
      )}
      {value.scheduleType === "interval" && (
        <div className="interval-fields">
          <label><span>間隔</span><div className="suffix-input"><input type="number" min={2} max={365} value={value.scheduleValue.days ?? 2} onChange={(event) => onChange({ ...value, scheduleValue: { ...value.scheduleValue, days: Number(event.target.value) } })} /><span>日ごと</span></div></label>
          <label><span>基準日</span><input type="date" value={value.scheduleValue.startDate ?? ""} onChange={(event) => onChange({ ...value, scheduleValue: { ...value.scheduleValue, startDate: event.target.value } })} /></label>
        </div>
      )}
    </div>
  );
}
